import {
    Box,
    Button,
    Grid,
    IconButton,
    MenuItem,
    Popper,
    Stack,
    TextField,
    ThumbnailAvatar,
    Typography,
} from "@biggeo/bg-ui/lab";
import { ChevronRightOutline, CloseOutline } from "@biggeo/bg-ui/lab/icons";
import isUndefined from "lodash/isUndefined";
import { useState } from "react";

import { PolygonFeature } from "../../utils/type";
import { useMap } from "../context";
import { createBufferedPolygon } from "../../utils/geometry-utils";
import { MapContextPolygon, MapContextPolygonSource } from "../context/types";
import { usePolygon } from "../context/hooks";

type ExpandedPolygon = {
    /**
     * The buffer distance in kilometers
     */
    readonly buffer: number;
    /**
     * A unique id for the expanded polygon
     */
    readonly id: string;
    /**
     * The polygon to be expanded
     */
    readonly polygon: MapContextPolygon;
};

export const ExpandRegionFunctionItem = ({
    originalPolygonId,
    selectedFeature,
    onClose,
}: {
    readonly originalPolygonId: string;
    readonly selectedFeature: PolygonFeature;
    readonly onClose: () => void;
}) => {
    const { map, draw } = useMap();

    const [expandedPolygon, setExpandedPolygon] = useState<ExpandedPolygon>();

    const [open, setOpen] = useState<"description" | "function">();

    const fillLayerId = `fill-${originalPolygonId}`;
    const lineLayerId = `line-${originalPolygonId}`;

    const { updatePolygon } = usePolygon();

    /**
     * Removes the expanded polygon from the map and the state.
     */
    const removeExpandedPolygon = () => {
        if (map.current?.getLayer(fillLayerId)) {
            map.current.removeLayer(fillLayerId);
        }
        if (map.current?.getLayer(lineLayerId)) {
            map.current.removeLayer(lineLayerId);
        }
        if (map.current?.getSource(originalPolygonId)) {
            map.current.removeSource(originalPolygonId);
        }

        setExpandedPolygon(undefined);
    };

    /**
     * Handles the change event for expanding a polygon feature.
     * Creates an expanded version of the original polygon using Turf.js.
     */
    const onChange = (value: number) => {
        /**
         * If the buffer distance is 0, remove the expanded polygon.
         */
        if (value === 0 && expandedPolygon) {
            return removeExpandedPolygon();
        }

        // Create the expanded polygon
        const newExpandedPolygon = createBufferedPolygon(
            selectedFeature,
            value,
            originalPolygonId
        );

        if (!newExpandedPolygon) return;

        /**
         * Check if the expanded polygon already exists in the state.
         * If it does, remove the polygon
         */
        if (expandedPolygon) {
            removeExpandedPolygon();
        }

        /**
         * Create a new input polygon from the buffered polygon with a unique ID.
         * Add the new polygon to the state.
         */
        const inputExpandedPolygon = {
            id: originalPolygonId,
            feature: newExpandedPolygon,
            source: MapContextPolygonSource.drawnShape,
            isSelected: true,
        };

        setExpandedPolygon({
            buffer: value,
            id: originalPolygonId,
            polygon: inputExpandedPolygon,
        });

        /**
         * Add a source for the buffered polygon.
         * Add fill and line layers for the buffered polygon, and point them to the added source.
         */
        map.current?.addSource(originalPolygonId, {
            type: "geojson",
            data: newExpandedPolygon,
        });
        map.current?.addLayer({
            id: fillLayerId,
            type: "fill",
            source: originalPolygonId,
            interactive: false,
            paint: {
                "fill-color": "#1570EF",
                "fill-opacity": 0.1,
            },
        });
        map.current?.addLayer({
            id: lineLayerId,
            type: "line",
            source: originalPolygonId,
            interactive: false,
            paint: {
                "line-color": "#2F5AF0",
                "line-width": 1,
                "line-dasharray": [4, 4],
            },
        });
    };

    /**
     * Replaces the original polygon with the expanded polygon.
     */
    const replaceOriginalPolygonWithExpanded = (
        originalPolygonId: string,
        expandedPolygon: ExpandedPolygon
    ) => {
        // Create the expanded polygon
        const newExpandedPolygon = createBufferedPolygon(
            selectedFeature,
            expandedPolygon.buffer,
            originalPolygonId
        );

        if (!newExpandedPolygon) return;

        /**
         * Remove the original polygon from the map.
         */
        draw.current?.delete(originalPolygonId);

        /**
         * Add the expanded polygon to the map.
         */
        draw.current?.add({
            ...newExpandedPolygon,
            properties: selectedFeature.properties,
        });

        /**
         * Replace the original polygon with the expanded polygon in the state.
         */
        updatePolygon(expandedPolygon.polygon);

        /**
         * Select the new shape
         */
        draw.current?.changeMode("simple_select", {
            featureIds: [expandedPolygon.polygon.id],
        });
    };

    const onClickApply = () => {
        /**
         * Add expanded polygon to the state.
         */
        if (expandedPolygon) {
            replaceOriginalPolygonWithExpanded(
                originalPolygonId,
                expandedPolygon
            );
        }

        setOpen(undefined);
        removeExpandedPolygon();
        onClose();
    };

    const onClickClose = () => {
        setOpen(undefined);
        removeExpandedPolygon();
        onClose();
    };

    return (
        <>
            <Popper
                open={!!open}
                placement="right-start"
                onOpenChange={(open) => {
                    if (!open) {
                        setOpen(undefined);
                        removeExpandedPolygon();
                    }
                    setOpen("description");
                }}
                content={
                    open === "function" ? (
                        <ExpandRegionFunction
                            onChange={onChange}
                            onClickApply={onClickApply}
                            onClickClose={onClickClose}
                        />
                    ) : (
                        <ExpandRegionDescription />
                    )
                }
                configuration={{
                    openOnHover: true,
                    preventCloseOnHoverAway: open === "function", // If function is open, don't close on hover away
                    preventCloseOnHoverBetweenTarget: true,
                }}
                removeFloatingArrow
            >
                <MenuItem
                    label="Expand Region"
                    startNode={
                        <ThumbnailAvatar
                            src="https://sabiggeomedia01.blob.core.windows.net/media/2eab4801-0d89-4a6b-9fac-9ae1f6e4ca8a.png"
                            square
                            size="xxs"
                        />
                    }
                    endNode={<ChevronRightOutline size="xs" />}
                    onClick={() => {
                        if (open === "description") {
                            setOpen("function");
                        } else {
                            setOpen("description");
                        }
                    }}
                />
            </Popper>
        </>
    );
};

const ExpandRegionDescription = () => {
    return (
        <Stack
            rowGap={1}
            sx={{
                padding: 2,
                borderRadius: 1,
                borderColor: (theme) => theme.palette.stroke[100],
                height: "100%",
                width: (theme) => theme.spacing(44.5),
            }}
        >
            <Typography variant="body4" fontWeight="semibold">
                Expand Region
            </Typography>
            <Typography
                variant="body4"
                sx={{
                    color: (theme) => theme.palette.disabled.onContainer,
                }}
            >
                Expand your selected area by a defined distance.
            </Typography>
            <Box
                sx={{
                    height: (theme) => theme.spacing(25),
                    backgroundImage:
                        "url(https://sabiggeomedia01.blob.core.windows.net/media/416847fd-3708-4341-9e8b-2e94c3fbdc1b.png)",
                    backgroundSize: "contain",
                }}
            />
        </Stack>
    );
};

const ExpandRegionFunction = ({
    onChange,
    onClickApply,
    onClickClose,
}: {
    readonly onChange: (value: number) => void;
    readonly onClickApply: () => void;
    readonly onClickClose: () => void;
}) => {
    const [bufferDistance, setBufferDistance] = useState<number | undefined>(0);

    const onChangeWrapper = (value?: number) => {
        setBufferDistance(value);
        onChange(value ?? 0);
    };

    return (
        <Stack
            rowGap={2}
            sx={{
                borderRadius: 1,
                border: 1,
                borderColor: (theme) => theme.palette.stroke[100],
                padding: 2,
                width: (theme) => theme.spacing(45),
            }}
        >
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                    <Grid container alignItems="center" columnGap={1}>
                        <Grid item>
                            <ThumbnailAvatar
                                src="https://sabiggeomedia01.blob.core.windows.net/media/2eab4801-0d89-4a6b-9fac-9ae1f6e4ca8a.png"
                                sx={{
                                    width: (theme) => theme.spacing(3.5),
                                    height: (theme) => theme.spacing(3.5),
                                }}
                            />
                        </Grid>
                        <Grid item>
                            <Typography variant="body4">
                                Expand Region
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item>
                    <IconButton variant="minimal" onClick={onClickClose}>
                        <CloseOutline size="sm" />
                    </IconButton>
                </Grid>
            </Grid>
            <Typography
                variant="body4"
                sx={{
                    color: (theme) => theme.palette.disabled.onContainer,
                }}
            >
                Enter distance
            </Typography>
            <TextField
                fullWidth
                type="number"
                value={!isUndefined(bufferDistance) ? `${bufferDistance}` : ""}
                onChange={(_, v) => {
                    if (v === "") {
                        onChangeWrapper(undefined);
                        return;
                    }

                    const valueNum = v ? Number.parseFloat(v) : 0;

                    if (valueNum < 0) {
                        return;
                    }

                    onChangeWrapper(valueNum);
                }}
            />
            <Grid container justifyContent="flex-end">
                <Button
                    variant="minimal"
                    color="primary"
                    onClick={onClickApply}
                    disabled={!bufferDistance || bufferDistance <= 0}
                >
                    Apply
                </Button>
            </Grid>
        </Stack>
    );
};
